import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Pagination from "../../shared/Pagination";
import LoanApplicationFilters from "../../components/Modals/filters/Loan_Application";
import { getLoanStatusCodes } from "../../sdk/loans/loans";
import { toast } from "react-toastify";
import useAudit from "../../hooks/useAudit";

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [loanStatusCodes, setLoanStatusCodes] = useState([]);
  const [isLoadingLoanStatuses, setIsLoadingLoanStatuses] = useState(false);
  const [loanStatusesError, setLoanStatusesError] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    type: [],
    status: "Pending_approval",
    duration: [],
    repaymentAmount: [],
  });

  const { logAudit } = useAudit();

  useEffect(() => {
    logAudit({
      category: "Loan Applications",
      actionCode: "VIEW_LOAN_APPLICATIONS",
      description: "Viewed loan applications",
      page: "Loan Applications",
      tab: "Loan Applications List",
      section: "Applications List",
      metadata: {
        route: "/dashboard/loan-applications",
      },
    });
  }, []);

  const [selectedFilter, setSelectedFilter] = useState("Status");
  const [selectedStatus, setSelectedStatus] = useState("Pending_approval");
  const [selectedLoanTypes, setSelectedLoanTypes] = useState([
    "Normal Loan",
    "Jijenge Loan",
    "Emergency Loan",
  ]);

  const [selectedDurations, setSelectedDurations] = useState([
    "3 – 6 Months",
    "6–12 Months",
    "> 12 Months",
  ]);

  const [selectedRepaymentAmounts, setSelectedRepaymentAmounts] = useState([
    "KES 1,001 – 10,000",
    "KES 10,001 – 25,000",
    "KES 25,001 – 50,000",
    "KES 50,001 – 100,000",
    "> KES 100,000",
  ]);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;

    if (selectedLoanTypes?.length > 0) {
      count += 1;
    }

    if (selectedStatus) {
      count += 1;
    }

    if (selectedDurations?.length > 0) {
      count += 1;
    }

    if (selectedRepaymentAmounts?.length > 0) {
      count += 1;
    }

    return count;
  }, [
    selectedLoanTypes,
    selectedStatus,
    selectedDurations,
    selectedRepaymentAmounts,
  ]);

  const [isLoanApplicationFilterOpen, setIsLoanApplicationFilterOpen] =
    useState(false);

  const openLoanApplicationFilter = () => {
    setIsLoanApplicationFilterOpen(true);
  };

  const closeLoanApplicationFilter = () => {
    setIsLoanApplicationFilterOpen(false);
  };

  /**
   * STATIC DATA
   * EASY TO REPLACE WITH API
   */
  const applicationsData = [
    {
      id: "#TA3305",
      member: "Lilian Kwamboka",
      amount: 100000,
      loanType: "Digital loan",
      duration: "3 months",
      createdAt: "11/10/2023",
      status: "Incomplete Application",
    },
    {
      id: "#TA3304",
      member: "Anita Bikambo",
      amount: 400000,
      loanType: "Digital loan",
      duration: "6 months",
      createdAt: "11/10/2023",
      status: "Pending Disbursement",
    },
    {
      id: "#TA3303",
      member: "Brenda Muthoni",
      amount: 200000,
      loanType: "Check-off loan",
      duration: "1 month",
      createdAt: "11/10/2023",
      status: "Pending Review",
    },
    {
      id: "#TA3302",
      member: "Austin Ombieji",
      amount: 50000,
      loanType: "Digital loan",
      duration: "6 months",
      createdAt: "11/10/2023",
      status: "Pending Disbursement",
    },
    {
      id: "#TA3301",
      member: "Rajesh Shah",
      amount: 600000,
      loanType: "Digital loan",
      duration: "12 months",
      createdAt: "11/10/2023",
      status: "Pending Review",
    },
    {
      id: "#TA3300",
      member: "Berly Akoth",
      amount: 1700000,
      loanType: "Check-off loan",
      duration: "24 months",
      createdAt: "11/10/2023",
      status: "Pending Disbursement",
    },
    {
      id: "#TA3299",
      member: "Solomon Awin",
      amount: 300000,
      loanType: "Digital loan",
      duration: "1 month",
      createdAt: "11/10/2023",
      status: "Pending Review",
    },
    {
      id: "#TA3298",
      member: "Sawaia Juma",
      amount: 70000,
      loanType: "Digital loan",
      duration: "6 months",
      createdAt: "11/10/2023",
      status: "Pending Review",
    },
  ];

  /**
   * EASY API INTEGRATION
   */
  const fetchApplications = async () => {
    try {
      /**
       * REPLACE WITH API
       * const response = await fetchApplicationsAPI();
       * setApplications(response?.data?.items);
       */

      setApplications(applicationsData);
    } catch (error) {
      console.log(error);
      setApplications([]);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchLoanStatusCodes = async () => {
    try {
      setIsLoadingLoanStatuses(true);
      setLoanStatusesError(false);

      const response = await getLoanStatusCodes();

      setLoanStatusCodes(response?.data?.data?.data?.status_data || []);
    } catch (error) {
      setLoanStatusCodes([]);
      setLoanStatusesError(true);

      toast?.error(
        error?.response?.data?.message || "Failed to fetch loan status codes",
      );
    } finally {
      setIsLoadingLoanStatuses(false);
    }
  };

  useEffect(() => {
    fetchLoanStatusCodes();
  }, []);

  const handleRefresh = async () => {
    setRefreshLoading(true);

    await fetchApplications();

    setTimeout(() => {
      setRefreshLoading(false);
    }, 800);
  };

  const filteredApplications = useMemo(() => {
    return applications?.filter(
      (item) =>
        item?.id?.toLowerCase()?.includes(query?.toLowerCase()) ||
        item?.member?.toLowerCase()?.includes(query?.toLowerCase()),
    );
  }, [applications, query]);

  const totalPages = Math?.ceil(filteredApplications?.length / limit);

  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;

    return filteredApplications?.slice(startIndex, endIndex);
  }, [filteredApplications, currentPage, limit]);

  /**
   * OVERVIEW CARDS
   */
  const overviewCards = [
    {
      title: "Incomplete Loans",
      value: "673",
      image: "/Vector.png",
    },
    {
      title: "Pending Review",
      value: "13,789",
      image: "/Vector_2.png",
    },
    {
      title: "Pending Disbursement",
      value: "917",
      image: "/Vector_3.png",
    },
  ];

  const getStatusStyles = (status) => {
    switch (status) {
      case "Incomplete Application":
        return "bg-[#FFF7CC] text-[#3B3B3B]";

      case "Pending Review":
        return "bg-[#D8ECFF] text-[#3B3B3B]";

      case "Pending Disbursement":
        return "bg-[#D8F5E1] text-[#3B3B3B]";

      default:
        return "bg-[#F4F4F4] text-[#3B3B3B]";
    }
  };

  const handleExportLoanApplications = () => {
    logAudit({
      category: "Loan Applications",
      actionCode: "EXPORT_LOAN_APPLICATIONS",
      description: "Exported loan applications",
      page: "Loan Applications",
      tab: "Loan Applications List",
      section: "Export",
      metadata: {
        route: "/dashboard/loan-applications",
        totalRecords: filteredApplications?.length,
      },
    });

    // Existing export logic here
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Anansi || Loans</title>
      </Helmet>

      <div className="w-full px-4 md:px-6 py-5 overflow-y-auto">
        {/* OVERVIEW */}
        <div>
          <p className="text-[#074073] text-[18px] font-[700]">Overview</p>
        </div>

        {/* OVERVIEW CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-5 w-[70%]">
          {overviewCards?.map((card, index) => (
            <div
              key={index}
              className="w-full min-h-[160px] rounded-[12px] border border-[#D7E7F4] bg-[#F9FCFF] flex flex-col items-center justify-center px-4 py-6"
            >
              <img
                height={30}
                width={30}
                src={card?.image}
                alt="Vector"
                className="object-contain"
              />

              {/* VALUE */}
              <p className="mt-4 text-[#074073] text-[32px] font-[700] leading-none text-center">
                {card?.value}
              </p>

              <p className="mt-3 text-[#074073] text-[12px] font-[500] text-center">
                {card?.title}
              </p>
            </div>
          ))}
        </div>

        {/* TITLE */}
        <div className="mt-8">
          <p className="text-[#074073] text-[22px] md:text-[28px] font-[700] uppercase">
            Loan Applications
          </p>
        </div>

        {/* ACTIONS */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mt-6">
          {/* LEFT */}
          <div className="flex flex-col md:flex-row gap-3 w-full xl:w-auto">
            {/* SEARCH */}
            <div className="relative w-full md:w-[420px]">
              <span className="absolute inset-y-0 left-0 pl-4 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="7"
                    stroke="#999999"
                    strokeWidth="2"
                  />
                  <path
                    d="M20 20L17 17"
                    stroke="#999999"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>

              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e?.target?.value);
                  setCurrentPage(1);
                }}
                placeholder="Search Loan id, Member name"
                className="w-full h-[48px] border border-[#CCCCCC] text-[#999999] rounded-[4px] pl-12 pr-4 text-[14px] md:text-[15px] focus:outline-none"
              />
            </div>

            {/* FILTER */}
            <button
              onClick={openLoanApplicationFilter}
              className="h-[48px] px-5 border border-[#CCCCCC] rounded-[6px] flex items-center justify-center gap-3 w-full md:w-auto"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4 6H20"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M7 12H17"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M10 18H14"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>

              <p className="text-[16px] font-[500] text-[#000000]">Filter</p>

              <div className="w-[24px] h-[24px] rounded-full bg-[#E8F0FF] flex items-center justify-center text-[#2563EB] text-[13px] font-[600]">
                {appliedFiltersCount || 0}
              </div>
            </button>
          </div>

          {/* RIGHT */}
          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            {/* REFRESH */}
            <button
              onClick={handleRefresh}
              className="h-[48px] bg-[#FAFAFA] px-5 border border-[#A6A6A6] rounded-[4px] flex items-center justify-center gap-3 w-full sm:w-auto"
            >
              {!refreshLoading ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="29"
                  height="29"
                  viewBox="0 0 29 29"
                  fill="none"
                >
                  <path
                    d="M14.5931 22.9583C12.232 22.9583 10.2318 22.1395 8.59252 20.5018C6.95402 18.8633 6.13477 16.8639 6.13477 14.5036C6.13477 12.1433 6.95402 10.1431 8.59252 8.50303C10.2318 6.86211 12.232 6.04166 14.5931 6.04166C16.0294 6.04166 17.3739 6.38281 18.6265 7.06511C19.88 7.74742 20.8901 8.69999 21.657 9.92282V6.04166H22.8653V12.3612H16.5458V11.1529H21.0166C20.3875 9.9486 19.5022 8.99684 18.3607 8.29761C17.2208 7.5992 15.965 7.24999 14.5931 7.24999C12.5792 7.24999 10.8674 7.95485 9.45768 9.36457C8.04796 10.7743 7.3431 12.4861 7.3431 14.5C7.3431 16.5139 8.04796 18.2257 9.45768 19.6354C10.8674 21.0451 12.5792 21.75 14.5931 21.75C16.1438 21.75 17.5434 21.3069 18.7921 20.4208C20.0407 19.5347 20.9167 18.3667 21.4202 16.9167H22.7022C22.1698 18.7259 21.1576 20.1852 19.6657 21.2944C18.1738 22.4037 16.4829 22.9583 14.5931 22.9583Z"
                    fill="#4A4747"
                  />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  className="w-[20px] h-[20px] text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              )}

              <p className="text-[#4A4747] text-[16px] font-[400]">REFRESH</p>
            </button>

            {/* EXPORT */}
            <button
              onClick={handleExportLoanApplications}
              className="h-[48px] bg-[#FAFAFA] px-5 border border-[#A6A6A6] rounded-[4px] flex items-center justify-center gap-3 w-full sm:w-auto"
            >
              <p className="text-[#4A4747] text-[16px] font-[400]">EXPORT</p>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="#111827"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="mt-6 bg-white  overflow-hidden">
          {/* TABLE HEADER */}
          <div className="hidden lg:flex h-[52px] bg-[#EAF8FF] items-center text-[13px] font-[600] text-[#1E293B] px-4">
            <div className="w-[4%] flex justify-center">
              <input type="checkbox" />
            </div>

            <div className="w-[12%]">LOAN APP ID</div>

            <div className="w-[18%]">MEMBER</div>

            <div className="w-[12%]">AMOUNT</div>

            <div className="w-[15%]">LOAN TYPE</div>

            <div className="w-[12%]">LOAN DURATION</div>

            <div className="w-[12%]">DATE CREATED</div>

            <div className="w-[20%] text-center">STATUS</div>
          </div>

          {/* DESKTOP TABLE ROWS */}
          <div className="">
            {paginatedApplications?.map((application, index) => (
              <div
                key={index}
                className="min-h-[64px] flex items-center px-4 text-[14px] text-[#111827]"
              >
                <div className="w-[4%] flex justify-center">
                  <input type="checkbox" />
                </div>

                <div className="w-[12%] text-[#0A84FF] font-[500] cursor-pointer">
                  {application?.id}
                </div>

                <div className="w-[18%] pr-2 truncate">
                  {application?.member}
                </div>

                <div className="w-[12%] pr-2">
                  KES {application?.amount?.toLocaleString()}
                </div>

                <div className="w-[15%] pr-2">{application?.loanType}</div>

                <div className="w-[12%]">{application?.duration}</div>

                <div className="w-[12%]">{application?.createdAt}</div>

                {/* STATUS */}
                <div className="w-[20%] flex justify-center">
                  <div
                    className={`w-full max-w-[190px] h-[42px] rounded-[2px] flex items-center px-4 text-[14px] font-[500] whitespace-nowrap overflow-hidden text-ellipsis ${getStatusStyles(
                      application?.status,
                    )}`}
                  >
                    {application?.status}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* FOOTER */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 px-4 py-5">
            <div className="flex flex-wrap items-center gap-4">
              <p className="text-[#074073] text-[14px]">Rows per page</p>

              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-[80px] h-[38px] border border-[#E5E7EB] rounded-[8px] px-3 focus:outline-none"
              >
                <option value={25}>25</option>
                <option value={20}>20</option>
                <option value={10}>10</option>
                <option value={5}>5</option>
              </select>

              <p className="text-[#074073] text-[14px]">
                {currentPage} - {totalPages} of {filteredApplications?.length}
              </p>
            </div>

            <div className="overflow-x-auto">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        </div>

        <LoanApplicationFilters
          id="id_R"
          isLoanApplicationFilterOpen={isLoanApplicationFilterOpen}
          closeLoanApplicationFilter={closeLoanApplicationFilter}
          loanStatusCodes={loanStatusCodes}
          isLoadingLoanStatuses={isLoadingLoanStatuses}
          loanStatusesError={loanStatusesError}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          appliedFiltersCount={appliedFiltersCount}
          selectedLoanTypes={selectedLoanTypes}
          setSelectedLoanTypes={setSelectedLoanTypes}
          selectedDurations={selectedDurations}
          setSelectedDurations={setSelectedDurations}
          selectedRepaymentAmounts={selectedRepaymentAmounts}
          setSelectedRepaymentAmounts={setSelectedRepaymentAmounts}
        />
      </div>
    </>
  );
};

export default Applications;
